<template>
  <tr class="mypage-ticket-item-detail-parts" ref="elm">
    <th class="mypage-ticket-item-detail-parts__name">{{ ticket.ticketName }}</th>
    <td class="mypage-ticket-item-detail-parts__meeting" v-if="ticket.meetingTime">
      <p class="mypage-ticket-item-detail-parts__meeting-container">
        <span class="mypage-ticket-item-detail-parts__meeting-label">{{
          $tc('MypagePage.MypageTicketListPage.meetingLabel')
        }}</span>
        <span class="mypage-ticket-item-detail-parts__meeting-time">{{ displayMeetingTime }}</span>
      </p>
    </td>
    <td class="mypage-ticket-item-detail-parts__count">
      ×{{ getTicketTotalCountByTicket(ticket, ownOrganization) }}
    </td>
    <td class="mypage-ticket-item-detail-parts__price">
      {{ displayTaxIncludedPrice }}{{ $tc('MypagePage.MypageTicketListPage.price.unit') }}
    </td>
  </tr>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import DigitalTicketDataTicketDocument from '@/store/stores/collectionModule/documents/digitalTicket/DigitalTicketDataTicketDocument'
import OrganizationDocument from '@/store/stores/collectionModule/documents/organization/OrganizationDocument'
import usePurchasedTicketData from '@/components/hook/ticket/usePurchasedTicketData'
import useDisplayDependingOnLang from '@/components/hook/useDisplayDependingOnLang'

/**
 * マイページ: チケット情報パーツ
 */
export default defineComponent({
  name: 'MypageTicketItemDetailParts',
  props: {
    /**
     * デジタルチケット チケット情報
     */
    ticket: {
      type: Object as PropType<DigitalTicketDataTicketDocument>,
      required: true,
    },
    /**
     * ログインユーザーの組織情報
     */
    ownOrganization: {
      type: Object as PropType<OrganizationDocument>,
      default: () => ({}),
    },
  },
  setup(props) {
    const { getTicketTotalCountByTicket } = usePurchasedTicketData()
    const { getDisplayDateJa } = useDisplayDependingOnLang()

    /**
     * 対象チケットの税込単価 × 購入枚数
     */
    const displayTaxIncludedPrice = computed(() => {
      const unitPrice = props.ownOrganization.ec?.purchasedProducts?.find(
        (product) => product.productNumber === props.ticket.productNumber,
      )?.unitPrice

      const totalCount = getTicketTotalCountByTicket(props.ticket, props.ownOrganization)

      return unitPrice ? (unitPrice * totalCount).toLocaleString() : ''
    })

    /**
     * 集合時間
     */
    const displayMeetingTime = computed(() =>
      getDisplayDateJa(props.ticket.meetingTime, 'HH:mm', 'HH:mm'),
    )

    return {
      getTicketTotalCountByTicket,
      displayTaxIncludedPrice,
      displayMeetingTime,
    }
  },
})
</script>

<style scoped lang="scss">
.mypage-ticket-item-detail-parts {
  width: 100%;
  height: 20px;
  border-bottom: 1px solid $black10;

  &__name {
    height: 20px;
    padding-top: 4px;
    padding-right: 8px;
    overflow: hidden;
    font-size: 12px;
    font-weight: 500;
    color: $black20;
  }

  &__meeting {
    height: 20px;
    padding-right: 8px;
    vertical-align: bottom;

    &-container {
      display: flex;
      gap: 6px;
      align-items: center;
      height: 16px;
      overflow: hidden;
      font-size: 12px;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &-label {
      font-size: 11px;
      color: $gray75;
      line-height: 1;
      letter-spacing: 0.02em;
      white-space: nowrap;
    }

    &-time {
      position: relative;
      min-width: 44px;
      font-size: 12px;
      line-height: 1;
      letter-spacing: 0.04em;
      text-align: right;
      white-space: nowrap;

      &::before {
        position: absolute;
        top: 50%;
        left: -2px;
        width: 2px;
        height: 8px;
        content: '';
        background: linear-gradient(
          84.94deg,
          #00ffba -121.14%,
          #00ffba -121.14%,
          rgba(0, 255, 186, 0.5) -80.24%,
          #00ffba -34.6%,
          rgba(0, 255, 186, 0.5) 10.88%,
          #00ffba 51.93%,
          rgba(0, 255, 186, 0.5) 91.87%
        );
        transform: translateY(-50%);
      }
    }
  }

  &__count {
    width: 24px;
    height: 20px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    vertical-align: bottom;
  }

  &__price {
    width: 78px;
    height: 20px;
    font-size: 12px;
    font-weight: 500;
    text-align: right;
    white-space: nowrap;
    vertical-align: bottom;
  }
}
</style>
